import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useReportsQuery } from '../../../../hooks/queries/useReportsQuery';
import { useAuth } from '../../../../hooks/useAuth';
import { DataItem } from '../../../../services/apiSLTP/activities';
import { User } from '../../../../services/apiSLTP/users';
import { capitalizeFirstLetter, formatName } from '../../../../utils/capitalize';
import { isSameDayOrAfter } from '../../../../utils/dates';
import { permissionLevels, reportTypeEnum } from '../../../../utils/enums';
import ModalBody, { ModalProps } from '../../ModalBody';
import CreateActivityModal from './CreateActivityModal';
import { Box, Card, CardContent, Typography, Stack, Divider, LinearProgress, IconButton as MIconButton } from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';

interface RemainingPeopleModalProps extends ModalProps {
    open: boolean;
    handleClose: () => void;
    users?: User[];
    activities: DataItem[];
    initDate: string;
    refetch: () => void;
    currentDate: Date;
}

const RemainingPeopleModal: React.FC<RemainingPeopleModalProps> = ({ open, handleClose, users, activities, initDate, refetch, currentDate }) => {
    const [selectedUserId, setSelectedUserId] = useState('');
    const [selectedActivityTypeId, setSelectedActivityTypeId] = useState('');
    const [showActivityModal, setShowActivityModal] = useState(false);

    const { t } = useTranslation();
    const { user: connectedUser } = useAuth();

    const { data: dataReports, isLoading } = useReportsQuery({ queries: { date: currentDate.toISOString() } });

    const canAdd = connectedUser && connectedUser.profileId.permsLevel <= permissionLevels.PLANNING;

    const groupedUsers = useMemo(() => {
        if (!users || !dataReports) return [];

        const activitiesUsers = activities.map((activity) => activity.reportId.userId._id);

        const remainingUsers = users
            .filter(
                (u) =>
                    !u.isSpecial &&
                    !activitiesUsers.includes(u._id) &&
                    (!u.arrivalDate || isSameDayOrAfter(currentDate, new Date(u.arrivalDate))) &&
                    dataReports?.find((r) => r.userId._id === u._id)?.reportType !== reportTypeEnum.ABSENT
            )
            .sort((a, b) => formatName(a.firstName, a.lastName).localeCompare(formatName(b.firstName, b.lastName)));

        const terrain = remainingUsers.filter((u) => u.profileId.permsLevel === permissionLevels.TERRAIN);
        const drivers = remainingUsers.filter((u) => u.profileId.permsLevel === permissionLevels.CHAUFFEUR);

        return [
            { type: 'terrain', label: capitalizeFirstLetter(t('terrain')), items: terrain },
            { type: 'drivers', label: capitalizeFirstLetter(t('drivers')), items: drivers },
        ];
    }, [users, activities, dataReports, currentDate, t]);

    return (
        <ModalBody open={open} handleClose={handleClose} title={t('remainingUsers')} closeText={t('cancel')} isOkVisible={false} okText="">
            <Box sx={{ maxHeight: 500, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 2 }}>
                {isLoading ? (
                    <LinearProgress />
                ) : (
                    groupedUsers.map(({ type, label, items }) => (
                        <Box key={type}>
                            <Typography variant="h6" sx={{ mb: 1 }}>
                                {label}
                            </Typography>
                            {items.length === 0 ? (
                                <Typography variant="body2" color="text.secondary">
                                    {capitalizeFirstLetter(t('noRemainingUser'))}
                                </Typography>
                            ) : (
                                items.map((u) => (
                                    <Card key={u._id} variant="outlined" sx={{ mb: 1, borderRadius: 1 }}>
                                        <CardContent sx={{ p: 1 }}>
                                            <Stack direction="row" justifyContent="space-between" alignItems="center">
                                                <Typography>{formatName(u.firstName, u.lastName)}</Typography>
                                                {canAdd && (
                                                    <MIconButton
                                                        color="primary"
                                                        size="small"
                                                        onClick={() => {
                                                            setSelectedUserId(u._id);
                                                            setSelectedActivityTypeId(type === 'drivers' ? '668e3980e29391daaf625cb5' : '');
                                                            setShowActivityModal(true);
                                                        }}
                                                    >
                                                        <AddCircleIcon />
                                                    </MIconButton>
                                                )}
                                            </Stack>
                                        </CardContent>
                                    </Card>
                                ))
                            )}
                        </Box>
                    ))
                )}
            </Box>

            {showActivityModal && (
                <CreateActivityModal
                    open={showActivityModal}
                    handleClose={() => setShowActivityModal(false)}
                    initDate={initDate}
                    refetch={refetch}
                    initUserId={selectedUserId}
                    initActivityTypeId={selectedActivityTypeId}
                    currentDate={currentDate}
                />
            )}
        </ModalBody>
    );
};

export default RemainingPeopleModal;
